import React, { useMemo } from "react";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import useApiData from "../services/useApiData.js";
import {
    AgingDistribution,
    BreakdownPanel,
    ChannelTable,
    KpiCard,
    KpiGrid,
    ReportContent,
    ReportError,
    ReportLoading,
    ReportNote,
    ReportScroll,
    ReportSection,
    ReportSplit,
    SpotlightStrip,
    formatCurrency,
    formatNumber,
} from "../components/Reporting/ReportPrimitives.jsx";

export default function ReportingInventory() {
    const { data, error, load } = useApiData("api/broylesllc/reports/inventory");

    const onHand = data?.onHand;
    const inTransit = data?.inTransit;
    const aging = data?.aging;

    const statusRows = useMemo(() => {
        const byStatus = inTransit?.byStatus || {};
        return Object.entries(byStatus).map(([channel, stats]) => ({
            channel,
            key: channel,
            ...stats,
        }));
    }, [inTransit]);

    const supplierRows = useMemo(() => {
        const bySupplier = inTransit?.bySupplier || {};
        return Object.entries(bySupplier)
            .map(([channel, stats]) => ({
                channel,
                key: channel,
                ...stats,
            }))
            .sort((a, b) => (b.value || 0) - (a.value || 0))
            .slice(0, 8);
    }, [inTransit]);

    const totalTracked = (onHand?.value || 0) + (inTransit?.value || 0);
    const onHandShare =
        totalTracked > 0 ? Math.round(((onHand?.value || 0) / totalTracked) * 100) : null;

    return (
        <CatalogPageShell
            title="Inventory"
            subtitle="Capital sitting on shelves, capital still in transit, and how long it has aged."
        >
            <ReportScroll>
                {error ? <ReportError message="Failed to load inventory report." /> : null}

                {load && !data ? (
                    <ReportLoading label="Rolling up inventory value…" />
                ) : data ? (
                    <ReportContent>
                        <SpotlightStrip
                            items={[
                                {
                                    label: "On hand",
                                    value: formatCurrency(onHand?.value),
                                    hint: `${formatNumber(onHand?.units, 0)} shelved units`,
                                },
                                {
                                    label: "In transit",
                                    value: formatCurrency(inTransit?.value),
                                    hint: `${formatNumber(inTransit?.invoiceCount, 0)} open invoices`,
                                },
                                {
                                    label: "Total tracked",
                                    value: formatCurrency(totalTracked),
                                    hint:
                                        onHandShare != null
                                            ? `${onHandShare}% currently on hand`
                                            : "On-hand + in-transit",
                                },
                            ]}
                        />

                        <ReportSection
                            eyebrow="Capital"
                            title="Inventory value"
                            subtitle="On-hand is shelved and unsold. In-transit is purchase invoices still open."
                        >
                            <KpiGrid>
                                <KpiCard
                                    label="On-hand value"
                                    value={formatCurrency(onHand?.value)}
                                    hint={`${formatNumber(onHand?.units, 0)} units`}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="In-transit value"
                                    value={formatCurrency(inTransit?.value)}
                                    hint={`${formatNumber(inTransit?.invoiceCount, 0)} invoices`}
                                />
                                <KpiCard
                                    label="In-transit units"
                                    value={formatNumber(inTransit?.units, 0)}
                                    hint="From invoice rollups"
                                />
                                <KpiCard
                                    label="On-hand share"
                                    value={onHandShare != null ? `${onHandShare}%` : "—"}
                                    hint="Of total tracked value"
                                />
                            </KpiGrid>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Freshness"
                            title="Aging status"
                            subtitle="How long on-hand inventory has sat since purchase — fresher capital turns faster."
                        >
                            <AgingDistribution buckets={aging?.buckets} />
                            {(aging?.missingPurchaseDate?.units || 0) > 0 ? (
                                <ReportNote>
                                    {formatNumber(aging.missingPurchaseDate.units, 0)} on-hand
                                    items ({formatCurrency(aging.missingPurchaseDate.value)}) are
                                    missing a purchase date and are excluded from aging.
                                </ReportNote>
                            ) : null}
                        </ReportSection>

                        <ReportSection
                            eyebrow="Pipeline"
                            title="In-transit breakdown"
                            subtitle="Open invoices awaiting pickup or already shipped to you."
                        >
                            <ReportSplit>
                                <BreakdownPanel title="By delivery status">
                                    <ChannelTable
                                        rows={statusRows}
                                        emptyLabel="No in-transit invoices right now."
                                        columns={[
                                            {
                                                key: "channel",
                                                label: "Status",
                                            },
                                            {
                                                key: "invoiceCount",
                                                label: "Invoices",
                                                align: "right",
                                                muted: true,
                                                render: (row) =>
                                                    formatNumber(row.invoiceCount, 0),
                                            },
                                            {
                                                key: "value",
                                                label: "Value",
                                                align: "right",
                                                primary: true,
                                                render: (row) => formatCurrency(row.value),
                                            },
                                        ]}
                                    />
                                </BreakdownPanel>
                                <BreakdownPanel title="Top suppliers">
                                    <ChannelTable
                                        rows={supplierRows}
                                        emptyLabel="No supplier pipeline to show."
                                        columns={[
                                            {
                                                key: "channel",
                                                label: "Supplier",
                                            },
                                            {
                                                key: "invoiceCount",
                                                label: "Invoices",
                                                align: "right",
                                                muted: true,
                                                render: (row) =>
                                                    formatNumber(row.invoiceCount, 0),
                                            },
                                            {
                                                key: "value",
                                                label: "Value",
                                                align: "right",
                                                primary: true,
                                                render: (row) => formatCurrency(row.value),
                                            },
                                        ]}
                                    />
                                </BreakdownPanel>
                            </ReportSplit>
                        </ReportSection>
                    </ReportContent>
                ) : null}
            </ReportScroll>
        </CatalogPageShell>
    );
}
